// src/pages/agent-booking-system-sabre-integration/components/SabreIntegration.jsx
import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SabreIntegration = ({ tenant, onInventoryUpdate }) => {
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [lastSync, setLastSync] = useState(new Date());
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [apiStats, setApiStats] = useState({
    requestsToday: 1247,
    successRate: 98.2,
    avgResponseTime: 245,
    errorsToday: 23
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    // Mock recent API activity
    setRecentActivity([
      {
        id: 1,
        type: 'search_request',
        endpoint: 'BargainFinderMax',
        status: 'success',
        responseTime: 180,
        timestamp: new Date(Date.now() - 2 * 60 * 1000)
      },
      {
        id: 2,
        type: 'availability_check',
        endpoint: 'EnhancedAirBook',
        status: 'success',
        responseTime: 95,
        timestamp: new Date(Date.now() - 5 * 60 * 1000)
      },
      {
        id: 3,
        type: 'price_quote',
        endpoint: 'FareQuote',
        status: 'success',
        responseTime: 320,
        timestamp: new Date(Date.now() - 8 * 60 * 1000)
      },
      {
        id: 4,
        type: 'booking_create',
        endpoint: 'CreatePassengerNameRecord',
        status: 'error',
        responseTime: 1200,
        timestamp: new Date(Date.now() - 15 * 60 * 1000),
        error: 'Authentication timeout'
      }
    ]);
  }, []);

  const handleManualSync = async () => {
    setSyncInProgress(true);
    
    try {
      // Simulate API sync
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      setLastSync(new Date());
      setApiStats(prev => ({
        ...prev,
        requestsToday: prev?.requestsToday + 1
      }));
      
      // Trigger inventory update callback
      onInventoryUpdate?.({
        eventType: 'SYNC',
        timestamp: new Date(),
        recordsUpdated: Math.floor(Math.random() * 500) + 100
      });
      
    } catch (error) {
      console.error('Sync error:', error);
    } finally {
      setSyncInProgress(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'warning': return 'text-orange-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return 'CheckCircle';
      case 'error': return 'XCircle';
      case 'warning': return 'AlertTriangle';
      default: return 'Circle';
    }
  };

  const formatResponseTime = (ms) => {
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000)?.toFixed(1)}s`;
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'search_request': return 'Search';
      case 'availability_check': return 'Calendar';
      case 'price_quote': return 'DollarSign';
      case 'booking_create': return 'Plus';
      default: return 'Activity';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-heading-bold text-text-primary mb-2">
          Sabre GDS Integration
        </h1>
        <p className="text-text-secondary">
          Monitor and manage your Sabre Global Distribution System connection
        </p>
      </div>
      {/* Connection Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2">
          <div className="bg-surface border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-heading-semibold text-text-primary">
                Connection Status
              </h2>
              <div className={`flex items-center space-x-2 ${getStatusColor(connectionStatus)}`}>
                <Icon name={getStatusIcon(connectionStatus)} size={20} />
                <span className="font-body-medium capitalize">{connectionStatus}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-body-medium text-text-primary mb-3">
                  Configuration
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Client ID:</span>
                    <span className="text-text-primary font-mono">SBR-{tenant?.id?.slice(-6) || '123456'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Environment:</span>
                    <span className="text-text-primary">Production</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">PCC:</span>
                    <span className="text-text-primary">9MYF</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Last Sync:</span>
                    <span className="text-text-primary">
                      {lastSync?.toLocaleTimeString()}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-body-medium text-text-primary mb-3">
                  Endpoints Status
                </h4>
                <div className="space-y-2">
                  {[
                    { name: 'BargainFinderMax', status: 'connected' },
                    { name: 'EnhancedAirBook', status: 'connected' },
                    { name: 'HotelAvail', status: 'connected' },
                    { name: 'VehicleAvail', status: 'warning' }
                  ]?.map((endpoint) => (
                    <div key={endpoint?.name} className="flex items-center justify-between">
                      <span className="text-text-secondary text-sm">{endpoint?.name}</span>
                      <div className={`flex items-center space-x-1 ${getStatusColor(endpoint?.status)}`}>
                        <Icon name={getStatusIcon(endpoint?.status)} size={14} />
                        <span className="text-xs capitalize">{endpoint?.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="text-sm text-text-secondary">
                  Auto-sync enabled • Next sync in 12 minutes
                </div>
                <button
                  onClick={handleManualSync}
                  disabled={syncInProgress}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white text-sm font-body-medium rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {syncInProgress ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Syncing...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="RefreshCw" size={16} />
                      <span>Manual Sync</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* API Stats */}
        <div>
          <div className="bg-surface border border-border rounded-lg p-6">
            <h2 className="text-lg font-heading-semibold text-text-primary mb-4">
              API Statistics
            </h2>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-text-secondary">Success Rate</span>
                  <span className="text-lg font-heading-semibold text-green-600">
                    {apiStats?.successRate}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full" 
                    style={{ width: `${apiStats?.successRate}%` }}
                  ></div>
                </div>
              </div>

              <div className="pt-4 border-t border-border space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-text-secondary">Requests Today</span>
                  <span className="font-body-medium text-text-primary">
                    {apiStats?.requestsToday?.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-text-secondary">Avg Response</span>
                  <span className="font-body-medium text-text-primary">
                    {apiStats?.avgResponseTime}ms
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-text-secondary">Errors Today</span>
                  <span className="font-body-medium text-red-600">
                    {apiStats?.errorsToday}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface border border-border rounded-lg">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-heading-semibold text-text-primary">
                Recent API Activity
              </h2>
              <button className="text-sm text-primary-600 hover:text-primary-700 font-body-medium">
                View All
              </button>
            </div>
          </div>
          
          <div className="divide-y divide-border">
            {recentActivity?.map((activity) => (
              <div key={activity?.id} className="p-4">
                <div className="flex items-start space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    activity?.status === 'success' ? 'bg-green-100' :
                    activity?.status === 'error'? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    <Icon 
                      name={getActivityIcon(activity?.type)} 
                      size={16} 
                      className={
                        activity?.status === 'success' ? 'text-green-600' :
                        activity?.status === 'error'? 'text-red-600' : 'text-gray-600'
                      }
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-body-medium text-text-primary">
                        {activity?.endpoint}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        activity?.status === 'success' ?'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
                      }`}>
                        {activity?.status}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-text-secondary">
                      <span>{formatResponseTime(activity?.responseTime)}</span>
                      <span>{activity?.timestamp?.toLocaleTimeString()}</span>
                    </div>
                    {activity?.error && (
                      <p className="text-xs text-red-600 mt-1">{activity?.error}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Health */}
        <div className="bg-surface border border-border rounded-lg">
          <div className="p-6 border-b border-border">
            <h2 className="text-lg font-heading-semibold text-text-primary">
              System Health
            </h2>
          </div>
          
          <div className="p-6">
            <div className="space-y-6">
              {/* Memory Usage */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-text-secondary">Memory Usage</span>
                  <span className="text-sm font-body-medium text-text-primary">67%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: '67%' }}></div>
                </div>
              </div>

              {/* CPU Usage */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-text-secondary">CPU Usage</span>
                  <span className="text-sm font-body-medium text-text-primary">23%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '23%' }}></div>
                </div>
              </div>

              {/* Connection Pool */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-text-secondary">Connection Pool</span>
                  <span className="text-sm font-body-medium text-text-primary">8/20</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '40%' }}></div>
                </div>
              </div>

              {/* Rate Limiting */}
              <div className="pt-4 border-t border-border">
                <h4 className="text-sm font-body-medium text-text-primary mb-3">
                  Rate Limits
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Hourly Limit:</span>
                    <span className="text-text-primary">1,247 / 5,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Daily Limit:</span>
                    <span className="text-text-primary">12,450 / 100,000</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Integration Info */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-body-medium text-blue-900 mb-2">
              Sabre Integration Features
            </h4>
            <div className="text-xs text-blue-700 space-y-1">
              <p>• Real-time flight availability and pricing from Sabre GDS</p>
              <p>• Automated inventory synchronization every 15 minutes</p>
              <p>• Corporate negotiated rates and fare rules integration</p>
              <p>• Booking confirmation and PNR management</p>
              <p>• Advanced search with flexible date and route options</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SabreIntegration;